import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { useVeri, dosyaSec, dosyaYukle, tarih, tarihSaat } from "@/lib/veri";
import { Band, Bos, Buton, Cip, Etiket, Hata, Kutu, Kv, Sayfa, etiket, girdi, tabloBas, tabloHucre } from "@/components/parcalar";
import { cevir } from "@/lib/dil";
import { useOturum } from "@/lib/oturum";
import { kisaTarih } from "@/lib/mrb-toplu";

interface Admin {
  batchler: { id: string; no: string; wo: string | null; varis: string | null; aktif: boolean; cevrim: string; riserlar: string[] }[];
  musteriler: { id: string; ad: string; soyad: string; eposta: string }[];
  sablonlar: { kod: string; ad: string; konu: string; govde: string; bilinmeyen: string[]; sistem: boolean }[];
  degiskenler: Record<string, string>;
  onizlemeler: Record<string, { kime: { ad: string; eposta: string }[]; cc: string[]; konu: string; govde: string }>;
}

export function Admin() {
  const { veri, hata, yenile, setVeri } = useVeri<Admin>("/admin");
  const { ben } = useOturum();
  const [mHata, setMHata] = useState<string | null>(null);
  const [m, setM] = useState({ ad: "", soyad: "", eposta: "" });
  const [sec, setSec] = useState<string | null>(null);
  const [taslak, setTaslak] = useState<{ ad: string; konu: string; govde: string } | null>(null);
  const [yeniSablon, setYeniSablon] = useState<{ acik: boolean; ad: string; kod: string; konu: string; govde: string }>({ acik: false, ad: "", kod: "", konu: "", govde: "" });
  const odak = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const [yeni, setYeni] = useState({ no: "", varis: "", wo: "" });
  const [rnler, setRnler] = useState<string[]>([]);
  /** Import hatası düğmenin yanında gösterilir; sayfa başındaki bant bu kutudan görünmüyordu. */
  const [importHata, setImportHata] = useState<string | null>(null);
  const [islem, setIslem] = useState<string | null>(null);

  /** Değişkeni son odaklanılan alanın imleç konumuna yerleştirir (konu ya da gövde). */
  const degiskenEkle = (k: string) => {
    const el = odak.current;
    const parca = `{{${k}}}`;
    if (!el) return;
    const bas = el.selectionStart ?? el.value.length;
    const son = el.selectionEnd ?? bas;
    const yeni = el.value.slice(0, bas) + parca + el.value.slice(son);
    const alan = el.dataset.alan as "ad" | "kod" | "konu" | "govde";
    if (el.dataset.hedef === "yeni") setYeniSablon((y) => ({ ...y, [alan]: yeni }));
    else setTaslak((t) => (t ? { ...t, [alan]: yeni } : t));
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(bas + parca.length, bas + parca.length); });
  };

  const dene = async (f: () => Promise<unknown>) => {
    setIslem(null);
    try { await f(); await yenile(); } catch (e) { setIslem(e instanceof Error ? e.message : cevir("Error")); }
  };

  if (hata) return <Hata mesaj={hata} />;
  if (!veri) return <Bos>{cevir("Loading...")}</Bos>;
  const t = sec ? veri.sablonlar.find((x) => x.kod === sec) : null;
  const on = sec ? veri.onizlemeler[sec] : null;

  return (
    <Sayfa ust="0.1 · E-06 / E-40" baslik={cevir("Admin — batch and customer management")} alt={<>{cevir("TP-OTC's \"new batch incoming\" notification comes via email/phone (channel is outside system); into system")} <b>{cevir("PM enters")}</b> {cevir("(Single Entry Gate). TP-OTC never accesses this screen.")}</>}>
      <Hata mesaj={islem} />
      {/* D-060: anahtar yönetimi yalnız sistem yöneticisinde (PM değil); sunucu da aynı kuralla 403 döner. */}
      {ben?.rol === "sistem_yoneticisi" && <DonusturucuAnahtarlari />}
      <Kutu baslik={cevir("Customer contacts (TP-OTC) — email recipients")} aksiyon={cevir("spare part request, damage, completion emails go to this list")}>
        <div className="scroll-thin overflow-x-auto"><table className="w-full min-w-[640px]"><thead><tr className="border-b border-line"><th className={tabloBas}>{cevir("First name")}</th><th className={tabloBas}>{cevir("Last name")}</th><th className={tabloBas}>{cevir("Email")}</th><th /></tr></thead>
          <tbody>{veri.musteriler.map((x) => <tr key={x.id} className="border-b border-line last:border-0"><td className={tabloHucre}>{x.ad}</td><td className={tabloHucre}>{x.soyad}</td><td className={tabloHucre}>{x.eposta}</td><td className={tabloHucre}><Buton kucuk islem="batch" onClick={() => dene(() => api.sil(`/admin/musteri/${x.id}`))}>{cevir("Delete")}</Buton></td></tr>)}</tbody></table></div>
        <div className="mt-3 grid gap-2 sm:grid-cols-4">
          <input className={girdi} placeholder={cevir("First name")} value={m.ad} onChange={(e) => setM({ ...m, ad: e.target.value })} />
          <input className={girdi} placeholder={cevir("Last name")} value={m.soyad} onChange={(e) => setM({ ...m, soyad: e.target.value })} />
          <input className={girdi} placeholder={cevir("name.surname@tp-otc.com")} value={m.eposta} onChange={(e) => setM({ ...m, eposta: e.target.value })} />
          <Buton islem="batch" tur="birincil" onClick={async () => { setMHata(null); try { await api.post("/admin/musteri", m); setM({ ad: "", soyad: "", eposta: "" }); await yenile(); } catch (e) { setMHata(e instanceof Error ? e.message : cevir("Error")); } }}>{cevir("Add customer")}</Buton>
        </div>
        <Hata mesaj={mHata} />
      </Kutu>

      <Kutu baslik={cevir("Email templates")} aksiyon={<span className="flex flex-wrap items-center gap-2">merge-tag: <Cip>{"{{degisken}}"}</Cip> {cevir("recipients are not written in template")} <Buton kucuk islem="batch" onClick={() => { setSec(null); setTaslak(null); setYeniSablon({ acik: !yeniSablon.acik, ad: "", kod: "", konu: "", govde: "" }); }}>{yeniSablon.acik ? cevir("Cancel") : cevir("+ New template")}</Buton></span>}>
        <div className="scroll-thin overflow-x-auto"><table className="w-full min-w-[640px]"><thead><tr className="border-b border-line"><th className={tabloBas}>{cevir("Template")}</th><th className={tabloBas}>{cevir("Subject line")}</th><th /></tr></thead>
          <tbody>{veri.sablonlar.map((x) => <tr key={x.kod} className="border-b border-line last:border-0"><td className={tabloHucre}><b>{x.ad}</b>{x.bilinmeyen.length ? <span className="ml-2 text-xs text-bad">{cevir("non-dictionary:")} {x.bilinmeyen.join(", ")}</span> : null}</td><td className={cn2(tabloHucre, "text-ink-2")}>{x.konu}</td><td className={tabloHucre}><Buton kucuk islem="batch" onClick={() => { const yeniSec = sec === x.kod ? null : x.kod; setSec(yeniSec); setTaslak(yeniSec ? { ad: x.ad, konu: x.konu, govde: x.govde } : null); }}>{sec === x.kod ? cevir("Close") : cevir("Edit")}</Buton></td></tr>)}</tbody></table></div>
        {yeniSablon.acik && (
          <div className="mb-3 space-y-2 rounded-md border border-dashed border-line-strong p-3">
            <div className="grid gap-2 sm:grid-cols-2">
              <div><label className={etiket}>{cevir("Template name")}</label><input className={girdi} placeholder={cevir("e.g. Shipment notification (TP-OTC)")} value={yeniSablon.ad} onChange={(e) => setYeniSablon({ ...yeniSablon, ad: e.target.value })} /></div>
              <div><label className={etiket}>{cevir("Code (generated from name if left empty)")}</label><input className={girdi} placeholder={cevir("shipment")} value={yeniSablon.kod} onChange={(e) => setYeniSablon({ ...yeniSablon, kod: e.target.value })} /></div>
            </div>
            <div><label className={etiket}>{cevir("Subject")}</label><input className={girdi} data-alan="konu" data-hedef="yeni" onFocus={(e) => (odak.current = e.currentTarget)} value={yeniSablon.konu} onChange={(e) => setYeniSablon({ ...yeniSablon, konu: e.target.value })} /></div>
            <div><label className={etiket}>{cevir("Body")}</label><textarea rows={7} data-alan="govde" data-hedef="yeni" onFocus={(e) => (odak.current = e.currentTarget)} className={cn2(girdi, "font-mono text-sm")} value={yeniSablon.govde} onChange={(e) => setYeniSablon({ ...yeniSablon, govde: e.target.value })} /></div>
            <DegiskenKutusu degiskenler={veri.degiskenler} ekle={degiskenEkle} />
            <Buton islem="batch" tur="birincil" disabled={!yeniSablon.ad || !yeniSablon.konu || !yeniSablon.govde} onClick={() => dene(async () => { await api.post("/admin/sablon", { ad: yeniSablon.ad, kod: yeniSablon.kod || undefined, konu: yeniSablon.konu, govde: yeniSablon.govde }); setYeniSablon({ acik: false, ad: "", kod: "", konu: "", govde: "" }); })}>{cevir("Save template")}</Buton>
          </div>
        )}
        {t && taslak && on && (
          <div className="mt-3 space-y-2 border-t border-line pt-3">
            <label className={etiket}>{cevir("Template name")}</label>
            <input className={girdi} value={taslak.ad} onChange={(e) => setTaslak({ ...taslak, ad: e.target.value })} />
            <label className={etiket}>{cevir("Subject")}</label>
            <input className={girdi} data-alan="konu" data-hedef="taslak" onFocus={(e) => (odak.current = e.currentTarget)} value={taslak.konu} onChange={(e) => setTaslak({ ...taslak, konu: e.target.value })} />
            <label className={etiket}>{cevir("Body")}</label>
            <textarea rows={9} data-alan="govde" data-hedef="taslak" onFocus={(e) => (odak.current = e.currentTarget)} className={cn2(girdi, "font-mono text-sm")} value={taslak.govde} onChange={(e) => setTaslak({ ...taslak, govde: e.target.value })} />
            <DegiskenKutusu degiskenler={veri.degiskenler} ekle={degiskenEkle} />
            <div className="flex flex-wrap gap-2">
              <Buton islem="batch" tur="birincil" onClick={() => dene(async () => { await api.patch(`/admin/sablon/${t.kod}`, taslak); })}>{cevir("Save")}</Buton>
              {!t.sistem && <Buton islem="batch" tur="tehlike" onClick={() => dene(async () => { await api.sil(`/admin/sablon/${t.kod}`); setSec(null); setTaslak(null); })}>{cevir("Delete template")}</Buton>}
              {t.sistem && <span className="self-center text-xs text-ink-3">{cevir("System template: linked to flows, cannot be deleted.")}</span>}
            </div>
            <div className="text-xs font-semibold uppercase tracking-wider text-ink-3">{cevir("Live preview (with sample data, saved state)")}</div>
            {!on.kime.length && <Band tip="not">{cevir("No active customer contact — no email is sent from this template. During testing, emails are sent only to allowlisted addresses (")}<code>MAIL_IZINLI</code>{cevir("); customer contacts are intentionally inactive.")}</Band>}
            <pre className="whitespace-pre-wrap rounded-md bg-surface-2 p-3 font-mono text-sm leading-relaxed">{cevir("To:")} {on.kime.map((k) => `${k.ad} <${k.eposta}>`).join(", ") || cevir("— (no active recipient)")}{"\n"}Cc: {on.cc.join(", ")}   {cevir("← automatic: PM + sender")}{"\n"}{cevir("Subject:")} {on.konu}{"\n\n"}{on.govde}</pre>
          </div>
        )}
      </Kutu>

      <div className="grid gap-4 md:grid-cols-2">
        {veri.batchler.map((b) => (
          <Kutu key={b.id} baslik={<>Batch {b.no} <span className="ml-1 font-normal text-ink-3">· {b.cevrim}</span></>} aksiyon={<Buton kucuk islem="batch" tur={b.aktif ? "birincil" : "normal"} onClick={() => dene(() => api.patch(`/admin/batch/${b.id}`, { aktif: !b.aktif }))}>{b.aktif ? cevir("Active ✓") : cevir("Inactive")}</Buton>} className={b.aktif ? "" : "opacity-60"}>
            <div className="grid grid-cols-2 gap-2 text-sm"><Kv ad={<>{cevir("Expected arrival")}</>}>{b.varis ?? "—"}</Kv><Kv ad={<>{cevir("Riser list")}</>}>{b.riserlar.length} RN · WO {b.wo ?? "—"}</Kv></div>
            <div className="mt-2 max-h-24 overflow-y-auto">{b.riserlar.map((r) => <Cip key={r}>{r}</Cip>)}</div>
          </Kutu>
        ))}
      </div>

      <Kutu baslik={cevir("Open new batch")}>
        <div className="grid gap-2 sm:grid-cols-3">
          <input className={girdi} placeholder="Batch no (B5)" value={yeni.no} onChange={(e) => setYeni({ ...yeni, no: e.target.value })} />
          <input className={girdi} placeholder={cevir("Expected arrival (±1-2 weeks)")} value={yeni.varis} onChange={(e) => setYeni({ ...yeni, varis: e.target.value })} />
          <input className={girdi} placeholder={cevir("WO (optional)")} value={yeni.wo} onChange={(e) => setYeni({ ...yeni, wo: e.target.value })} />
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
          <b>{cevir("Riser list — Excel/CSV import (single column: RN)")}</b>
          <span>{rnler.length ? rnler.map((r) => <Cip key={r}>{r}</Cip>) : <Cip>—</Cip>}</span>
          <Buton kucuk islem="batch" onClick={async () => { const f = await dosyaSec(".xlsx,.xls,.csv"); if (!f) return; setImportHata(null); try { const r = await dosyaYukle<{ riserlar: string[] }>("/admin/batch/import", f); setRnler(r.riserlar); } catch (e) { setRnler([]); setImportHata(e instanceof Error ? e.message : cevir("Error")); } }}>Import</Buton>
        </div>
        {importHata && <Band tip="duvar" className="mt-2">{importHata}</Band>}
        <div className="mt-3"><Buton islem="batch" tur="birincil" disabled={!rnler.length || !yeni.no} title={!rnler.length ? cevir("Batch cannot be opened without importing riser list") : undefined} onClick={() => dene(async () => { await api.post("/admin/batch", { ...yeni, riserlar: rnler }); setRnler([]); setYeni({ no: "", varis: "", wo: "" }); })}>{cevir("Open batch")}</Buton></div>
        <Band tip="bilgi" className="mt-3">{cevir("Entire file is rejected if even one row is corrupted (partial import prohibited). If the same riser arrives again after 5 years, a new cycle record is opened; data does not flow cumulatively.")}</Band>
      </Kutu>
      <DemoKutusu />

      {!veri && <Bos>—</Bos>}
      {void setVeri}
    </Sayfa>
  );
}

interface DonusturucuAnahtari { id: string; ad: string; olusturan: string; olusturmaTs: string; sonBaglanti: string | null; iptalTs: string | null }

/**
 * D-060, D-061 "Converter keys" (eski başlığı "MRB converter keys"): dönüştürücü bilgisayarın anahtarları. Anahtarın kendisi yalnız oluşturma yanıtında gelir ve burada
 * bir kez gösterilir (sunucu yalnız özetini saklar); kaybolursa iptal edilip yenisi oluşturulur. Yalnız sistem yöneticisine çizilir.
 */
function DonusturucuAnahtarlari() {
  const { veri, hata, yenile } = useVeri<DonusturucuAnahtari[]>("/donusturucu-anahtar");
  const [ad, setAd] = useState("");
  const [yeni, setYeni] = useState<string | null>(null);
  const [kopyalandi, setKopyalandi] = useState(false);
  const [mesgul, setMesgul] = useState<string | null>(null);
  const [islem, setIslem] = useState<string | null>(null);

  const olustur = async () => {
    setIslem(null);
    setMesgul("yeni");
    try {
      const r = await api.post<{ id: string; ad: string; anahtar: string }>("/donusturucu-anahtar", { ad: ad.trim() });
      setYeni(r.anahtar);
      setKopyalandi(false);
      setAd("");
      await yenile();
    } catch (e) {
      setIslem(e instanceof Error ? e.message : cevir("Operation failed"));
    } finally {
      setMesgul(null);
    }
  };

  const iptal = async (a: DonusturucuAnahtari) => {
    if (!window.confirm(cevir("Revoke the key \"{0}\"? The converter using it stops working at its next connection.", a.ad))) return;
    setIslem(null);
    setMesgul(a.id);
    try {
      await api.post(`/donusturucu-anahtar/${a.id}/iptal`);
      await yenile();
    } catch (e) {
      setIslem(e instanceof Error ? e.message : cevir("Operation failed"));
    } finally {
      setMesgul(null);
    }
  };

  const kopyala = async () => {
    if (!yeni) return;
    try {
      await navigator.clipboard.writeText(yeni);
      setKopyalandi(true);
    } catch {
      setIslem(cevir("The key could not be copied; select it and copy it by hand."));
    }
  };

  return (
    <Kutu baslik={cevir("Converter keys")} aksiyon={cevir("System Administrator only")}>
      <p className="text-sm text-ink-2">{cevir("A key lets the converter computer do the same five things for every document type it converts (MRBs, Disassembly reports, Disposition reports, Rework reports and Assembly reports): take the next document in the queue, extend its hold, download the PDF of the document it holds, deliver the excel and report a failure. While it holds a Disassembly, Disposition, Rework or Assembly report it may also download one auxiliary input: the content excel of the single MRB whose SN is exactly the SN read for that report; every such download is written to the event log. Nothing else: it cannot open other screens, search or list records, change records in any other way or upload PDFs. This site keeps only a fingerprint of the key; the key itself is shown once and stored on the converter computer, outside the repository. The test site and the live site have separate keys.")}</p>
      {(hata ?? islem) && <div className="mt-3"><Hata mesaj={hata ?? islem} /></div>}
      <div className="scroll-thin mt-3 overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead><tr className="border-b border-line"><th className={tabloBas}>{cevir("Name")}</th><th className={tabloBas}>{cevir("Created")}</th><th className={tabloBas}>{cevir("Last connected")}</th><th className={tabloBas}>{cevir("Status")}</th><th /></tr></thead>
          <tbody>
            {(veri ?? []).map((a) => (
              <tr key={a.id} className="border-b border-line last:border-0">
                <td className={tabloHucre}><b>{a.ad}</b></td>
                <td className={cn2(tabloHucre, "text-ink-2")}>{tarih(a.olusturmaTs)} · {a.olusturan}</td>
                <td className={cn2(tabloHucre, "text-ink-2")}>{a.sonBaglanti ? tarihSaat(a.sonBaglanti) : cevir("never")}</td>
                <td className={tabloHucre}>{a.iptalTs ? <Etiket>{cevir("revoked {0}", kisaTarih(a.iptalTs))}</Etiket> : <Etiket tone="ok">{cevir("active")}</Etiket>}</td>
                <td className={tabloHucre}>{!a.iptalTs && <Buton kucuk mesgul={mesgul === a.id} title={cevir("The converter stops working at its next connection")} onClick={() => iptal(a)}>{cevir("Revoke")}</Buton>}</td>
              </tr>
            ))}
            {veri && !veri.length && <tr><td colSpan={5}><Bos>{cevir("No converter key yet.")}</Bos></td></tr>}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex flex-wrap items-end gap-2">
        <div className="min-w-0 max-w-[320px] flex-1"><label className={etiket}>{cevir("Key name")}</label><input className={girdi} maxLength={80} placeholder={cevir("e.g. Converter laptop")} value={ad} onChange={(e) => setAd(e.target.value)} /></div>
        <Buton disabled={!ad.trim()} mesgul={mesgul === "yeni"} onClick={olustur}>{cevir("+ New key")}</Buton>
      </div>
      {yeni && (
        <Band tip="not" className="mt-3">
          <b>{cevir("New key (shown once):")}</b> <code className="break-all">{yeni}</code> <Buton kucuk onClick={kopyala}>{kopyalandi ? cevir("Copied") : cevir("Copy")}</Buton>
          <br />
          {cevir("Copy it now into the converter computer's key file. It will not be shown again; if it is lost, revoke it and create a new one.")}
        </Band>
      )}
    </Kutu>
  );
}

interface DemoSayim { operasyon: { batch: number; riser: number; komponent: number; tamir: number; dispo: number; olay: number; dokuman: number }; kutuk: { wps: number; sarf: number; kaynakci: number; alet: number; stok: number; stdGorev: number; rota: number; kanonik: number } }

const ONAY = { temizle: cevir("DELETE DEMO DATA"), yukle: cevir("LOAD DEMO DATA") } as const;

/**
 * Demo veri yönetimi. Çift onaylı: önce kutu işaretlenir, sonra onay cümlesi harfi harfine yazılır.
 * Kullanıcılar, mail şablonları ve müşteri kişileri hiçbir işlemde silinmez.
 */
function DemoKutusu() {
  const { veri, yenile } = useVeri<DemoSayim>("/admin/demo");
  const [mod, setMod] = useState<"temizle" | "yukle" | null>(null);
  const [kutu, setKutu] = useState(false);
  const [cumle, setCumle] = useState("");
  /** D-020: temizlik kapsamı — yalnız operasyonel · + demo örnek kütükler · + bütün kütükler */
  const [kutukler, setKutukler] = useState<false | "ornek" | "tum">(false);
  const [mesgul, setMesgul] = useState(false);
  const [sonuc, setSonuc] = useState<string | null>(null);
  const [hata, setHata] = useState<string | null>(null);

  const kapat = () => { setMod(null); setKutu(false); setCumle(""); setKutukler(false); };
  const calistir = async () => {
    if (!mod) return;
    setMesgul(true); setHata(null); setSonuc(null);
    try {
      const s = await api.post<DemoSayim>(`/admin/demo/${mod}`, { onay: cumle.trim(), kutukler });
      setSonuc(mod === "temizle" ? cevir`Cleared. Remaining: ${s.operasyon.riser} riser, ${s.operasyon.tamir} repair Item.` : cevir`Loaded: ${s.operasyon.riser} riser, ${s.operasyon.tamir} repair Items, ${s.kutuk.wps} WPS.`);
      await yenile();
      kapat();
    } catch (e) {
      setHata(e instanceof Error ? e.message : cevir("Operation failed"));
    } finally {
      setMesgul(false);
    }
  };

  const o = veri?.operasyon;
  const k = veri?.kutuk;
  return (
    <Kutu baslik={cevir("Demo data")} aksiyon={o ? cevir`${o.riser} riser · ${o.tamir} repair Items` : "—"}>
      <p className="text-sm text-ink-2">
        {cevir("Sample flow from prototype: batches, risers, Disassembly, Disposition and repair Items. Cleared when transitioning to real data entry; reloaded for presentation or training.")} <b>{cevir("Users, email templates and customer contacts are never deleted in any operation.")}</b>
      </p>
      {veri && (
        <div className="mt-3 grid gap-x-4 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-4">
          <Kv ad={cevir("Operational record")}>{o!.batch} batch · {o!.riser} riser · {o!.komponent} {cevir("component")} · {o!.dokuman} {cevir("document")}</Kv>
          <Kv ad={cevir("Repair chain")}>{o!.tamir} {cevir("Item ·")} {o!.dispo} disposition</Kv>
          <Kv ad={cevir("Audit log")}>{o!.olay} {cevir("rows (cleared with the operational records)")}</Kv>
          <Kv ad={cevir("Registers")}>{k!.wps} WPS · {k!.sarf} {cevir("Consumable ·")} {k!.kaynakci} {cevir("Welder ·")} {k!.stok} {cevir("stock")}</Kv>
        </div>
      )}

      <Hata mesaj={hata} />
      {sonuc && <Band tip="ok" className="mt-3">{sonuc}</Band>}

      {!mod ? (
        <div className="mt-3 flex flex-wrap gap-2">
          <Buton tur="tehlike" onClick={() => setMod("temizle")}>{cevir("Clear demo data")}</Buton>
          <Buton onClick={() => setMod("yukle")}>{cevir("Reload demo data")}</Buton>
          {/* Kütükleri tek başına doldurur: operasyonel kayıtlara DOKUNMAZ, dolu kütüğü ezmez.
              "Demo veriyi yeniden yükle" önce her şeyi sildiği için üzerinde çalışılan triyaj da
              gidiyordu; kütükler silinince rota dropdown'ı ve repair zinciri çalışmaz hale geliyor. */}
          <Buton mesgul={mesgul} onClick={async () => {
            setMesgul(true); setHata(null); setSonuc(null);
            try {
              const r = await api.post<{ yazilan: string[]; atlanan: string[] }>("/admin/demo/kutuk", {});
              setSonuc(r.yazilan.length ? cevir`Registers completed: ${r.yazilan.join(", ")}.${r.atlanan.length ? cevir` Skipped because they are full: ${r.atlanan.join(", ")}.` : ""}` : cevir`All Registers are already full (${r.atlanan.join(", ")}); no changes made.`);
              await yenile();
            } catch (e) { setHata(e instanceof Error ? e.message : cevir("Operation failed")); } finally { setMesgul(false); }
          }}>{cevir("Complete Registers (does not affect data)")}</Buton>
        </div>
      ) : (
        <div className="mt-3 space-y-3 rounded-md border border-dashed border-bad/50 bg-bad-bg/40 p-3">
          <p className="text-sm">
            {mod === "temizle"
              ? <>{cevir("Batch, riser, component, Receiving, Disassembly, MRB records and their content, field steps, inspection sets, Disposition, repair chain and set records are deleted, and so are uploaded document records, the audit log, the mail log and the MRB request flag. The cleanup itself is written as the first audit entry.")} {cevir("A file uploaded again afterwards opens a fresh record.")}</>
              : cevir("First, existing operational records and Registers will be deleted, then demo flow will be written from scratch. If you entered real data, it will be lost.")}
          </p>
          {mod === "temizle" && (
            <div className="space-y-1.5 text-sm">
              <div className="text-xs text-ink-3">{cevir("Scope")}</div>
              <label className="flex items-start gap-2"><input type="radio" name="kapsam" className="mt-0.5" checked={kutukler === false} onChange={() => setKutukler(false)} /> <span>{cevir("Operational records only")}</span></label>
              <label className="flex items-start gap-2"><input type="radio" name="kapsam" className="mt-0.5" checked={kutukler === "ornek"} onChange={() => setKutukler("ornek")} /> <span><b>{cevir("Operational records + demo sample Registers")}</b> {cevir("(WPS, Consumable, Welder, instrument, stock, canonical SN). The ST register, route templates, Disassembly MRB rules, email templates, users and customer contacts stay.")}</span></label>
              <label className="flex items-start gap-2"><input type="radio" name="kapsam" className="mt-0.5" checked={kutukler === "tum"} onChange={() => setKutukler("tum")} /> <span>{cevir("Operational records + all Registers (ST register and route templates too)")}</span></label>
            </div>
          )}
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" checked={kutu} onChange={(e) => setKutu(e.target.checked)} className="mt-0.5" />
            <span>{cevir("I understand that this operation cannot be undone")}{mod === "temizle" ? "" : cevir(" and accept that existing records will be deleted")}.</span>
          </label>
          <div>
            <label className={etiket}>{cevir("Second confirmation:")} <code>{ONAY[mod]}</code> {cevir("type")}</label>
            <input type="text" className={girdi} value={cumle} onChange={(e) => setCumle(e.target.value)} placeholder={ONAY[mod]} autoComplete="off" />
          </div>
          <div className="flex flex-wrap gap-2">
            <Buton tur={mod === "temizle" ? "tehlike" : "birincil"} disabled={!kutu || cumle.trim() !== ONAY[mod] || mesgul} onClick={calistir}>
              {mesgul ? cevir("Working...") : mod === "temizle" ? cevir("Clear") : cevir("Upload")}
            </Buton>
            <Buton onClick={kapat}>{cevir("Cancel")}</Buton>
          </div>
        </div>
      )}
    </Kutu>
  );
}

/** Değişken kutusu: tıklanınca son odaklanılan alanın imlecine yerleşir. */
function DegiskenKutusu({ degiskenler, ekle }: { degiskenler: Record<string, string>; ekle: (k: string) => void }) {
  return (
    <div className="rounded-md border border-line bg-surface-2 p-2">
      <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-ink-3">{cevir("Variable dictionary — click to insert at cursor")}</div>
      <div className="flex flex-wrap gap-1">
        {Object.entries(degiskenler).map(([k, v]) => (
          <button key={k} type="button" title={v} onMouseDown={(e) => e.preventDefault()} onClick={() => ekle(k)} className="rounded border border-line bg-surface px-1.5 py-0.5 font-mono text-xs hover:border-orange hover:text-orange">{`{{${k}}}`}</button>
        ))}
      </div>
      <p className="mt-1 text-xs text-ink-3">{cevir("Recipients are not written in template; they come from customer contacts list. Cc automatic: PM + sender.")}</p>
    </div>
  );
}

function cn2(...a: (string | false | undefined)[]) {
  return a.filter(Boolean).join(" ");
}
